/**
 * O campo único da busca no SISAC: CPF, nome e sobrenome, ou código.
 *
 * - Só dígitos: o código do paciente. Com 11 dígitos é o CPF (o código do
 *   SISAC tem 6), e o campo ganha a máscara; nenhum dígito é cortado.
 * - Texto: nome e sobrenome, duas palavras de ao menos duas letras. O nome
 *   sozinho devolveria meia clínica (decisão de 25/10).
 *
 * A busca por nome ou código só existe com a parte ligada e a marca do
 * prontuário (`pode.buscar_por_termo`); sem ela, o campo aceita só CPF.
 */

import { MINIMO_DO_CODIGO_DO_PACIENTE, temNomeESobrenome } from '@cliqcrm/contracts';
import { mascararCpf, soDigitos } from '@/entities/saude/paciente';

export interface PedidoDaBusca {
  readonly cpf: string | null;
  readonly termo: string | null;
}

const soNumeros = (texto: string) => /^[\d.\-\s]*$/.test(texto);

/** O que o campo mostra enquanto a pessoa digita: a máscara só no CPF completo. */
export function textoDaBusca(valor: string, porTermo: boolean): string {
  if (!porTermo) return mascararCpf(valor);
  return soNumeros(valor) && soDigitos(valor).length === 11 ? mascararCpf(valor) : valor;
}

/**
 * O corpo da busca, ou nulo quando ainda não dá para procurar. Campo vazio
 * procura só pelo telefone da conversa, se houver.
 */
export function pedidoDaBusca(
  texto: string,
  { porTermo, comTelefone }: { porTermo: boolean; comTelefone: boolean },
): PedidoDaBusca | null {
  const limpo = texto.trim().replace(/\s+/g, ' ');
  if (limpo === '') return comTelefone ? { cpf: null, termo: null } : null;
  const digitos = soDigitos(limpo);
  if (soNumeros(limpo)) {
    if (digitos.length === 11) return { cpf: digitos, termo: null };
    return porTermo && digitos.length >= MINIMO_DO_CODIGO_DO_PACIENTE
      ? { cpf: null, termo: digitos }
      : null;
  }
  return porTermo && temNomeESobrenome(limpo) ? { cpf: null, termo: limpo } : null;
}
